import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { LoadingScreen } from './LoadingScreen/LoadingScreen';
import { getKnowledgeDoc, getMessageContent, KnowledgeDocRecord } from '../services/knowledgeDocsService';
import { formatKnowledgeDocTitle, parseKnowledgeDocs, KnowledgeDoc } from '../utils/parseKnowledgeDocs';

// Standalone viewer for a knowledge document, in two modes:
//  - Saved:   /project/:projectId/knowledge-doc/:knowledgeDocId   (:knowledgeDocId = Mongo _id)
//             Supports public access when readonly_sharable_link_enabled=true.
//  - Unsaved: /project/:projectId/message/:messageId/knowledge-doc/:knowledgeDocId
//             (:knowledgeDocId = the <KnowledgeDoc id="..."> tag id) — read from the message,
//             no DB write. Not shareable until saved.
const KnowledgeDocViewer: React.FC = () => {
  const { projectId, knowledgeDocId, messageId } = useParams<{
    projectId: string;
    knowledgeDocId: string;
    messageId?: string;
  }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<{ title: string; content: string; version?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId || !knowledgeDocId) {
      setError('Invalid URL parameters');
      setLoading(false);
      return;
    }
    fetchDoc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, knowledgeDocId, messageId]);

  const fetchDoc = async () => {
    try {
      setLoading(true);
      setError(null);

      if (messageId) {
        // Unsaved: pull the message content and extract the matching KnowledgeDoc block.
        const { content } = await getMessageContent(projectId!, messageId);
        const { parts } = parseKnowledgeDocs(content);
        const match = parts.find(
          (p): p is KnowledgeDoc => typeof p !== 'string' && p.identifier === knowledgeDocId,
        );
        if (!match) {
          setError('Knowledge document not found in this message');
          return;
        }
        setDoc({ title: match.title, content: match.content, version: match.version });
        return;
      }

      const data: KnowledgeDocRecord = await getKnowledgeDoc(projectId!, knowledgeDocId!);
      setDoc({ title: data.title, content: data.content, version: data.version });
    } catch (err: any) {
      console.error('Error fetching knowledge document:', err);
      if (err.response?.status === 404) {
        setError('Knowledge document not found or you do not have access to it');
      } else if (err.response?.status === 401) {
        setError('You are not authorized to view this knowledge document');
      } else {
        setError('Failed to load knowledge document. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen w-screen">
        <LoadingScreen />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="h-screen w-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="text-text-error-primary text-lg font-medium mb-2">Error</div>
          <div className="text-text-secondary mb-4">{error || 'Knowledge document not found'}</div>
          <button
            onClick={() => navigate(`/chat/${projectId}`)}
            className="px-4 py-2 bg-bg-brand-solid text-text-primary-on-brand rounded hover:bg-bg-brand-solid-hover transition-colors"
          >
            Back to Project
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-bg-primary overflow-auto">
      <div className="max-w-4xl mx-auto px-8 py-10">
        <h1 className="text-2xl font-semibold text-text-primary mb-6">
          {formatKnowledgeDocTitle(doc.title, doc.version)}
        </h1>
        <div className="prose prose-lg max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={{
              code({ className, children, ...props }: any) {
                const isInline = !className?.includes('language-');
                return isInline ? (
                  <code className="bg-bg-secondary px-1 py-0.5 rounded text-sm font-mono" {...props}>
                    {children}
                  </code>
                ) : (
                  <code className="block bg-bg-secondary p-3 rounded text-sm font-mono overflow-x-auto" {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {doc.content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeDocViewer;
